import { LoadingOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { ReactComponent as DiscordIcon } from '@assets/icons/V4/discord-icon.svg';
import { ReactComponent as MailIcon } from '@assets/icons/V4/mail-icon.svg';
import { ReactComponent as PhoneIcon } from '@assets/icons/V4/phone-icon.svg';
import { HoverShinyEffect } from '@components';
import apiClient from '@contexts/AuthContext/apiClient';
import {
  DISCORD_MENTOR_CHANNEL,
  DISCORD_STUDENT_CHANNEL,
  SUPPORT_UPGRADE_EDUCATION_EMAIL,
  UPGRADE_EDUCATION_PHONE_NUMBER,
  UPGRADE_EDUCATION_PHONE_NUMBER_DISPLAY,
} from '@shared/constants';
import { ErrorCodes } from '@shared/enums';
import { BASE_URL, USE_AUTH0 } from '@shared/frontendEnv';
import v3global from '@styles/v3/global-v3.module.scss';
import { Input, Alert } from 'antd';

import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import useToast from '../../hooks/useToast';
import { logger } from '../../utils/logger';
import { COLORS } from '../../v3/global';

const PreOnboardingPage = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [id, setId] = useState<any>('');
  const [registrationToken, setRegistrationToken] = useState('');
  const [isMentor, setIsMentor] = useState(false);
  const [isRegistrationTokenValidated, setIsRegistrationTokenValidated] = useState(Boolean);
  const [error, setError] = useState();

  const toast = useToast();

  const [authFlag] = useState<boolean>(USE_AUTH0 === '1');

  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);

  const validatePassword = (password: string): string[] => {
    const errors: string[] = [];

    if (!password.trim()) {
      errors.push('"Password" cannot be empty.');
    }

    if (!confirmPassword.trim()) {
      errors.push('"Confirm Password" cannot be empty.');
    }

    if (password !== confirmPassword) {
      errors.push('Passwords do not match');
    }

    if (password.length < 8) {
      errors.push('Password must be at least 8 characters long.');
    }

    // Check if the password contains at least one lowercase letter, one uppercase letter, one number, and one special character
    const lowercaseRegex = /[a-z]/;
    const uppercaseRegex = /[A-Z]/;
    const numberRegex = /\d/;
    const specialCharacterRegex = /[!@#$%^&*()_+{}[\]:;<>,.?~\\/-]/;

    if (!lowercaseRegex.test(password)) {
      errors.push('Password must contain at least one lowercase letter.');
    }

    if (!uppercaseRegex.test(password)) {
      errors.push('Password must contain at least one uppercase letter.');
    }

    if (!numberRegex.test(password)) {
      errors.push('Password must contain at least one number.');
    }

    if (!specialCharacterRegex.test(password)) {
      errors.push('Password must contain at least one special character.');
    }

    return errors;
  };

  const handlePasswordChange = (newPassword: string) => {
    setPassword(newPassword);
  };

  const checkURLquery = async () => {
    const params = new URLSearchParams(window.location.search);

    const id = params.get('id');
    const token = params.get('token');

    if (!id || !token) return;

    setId(id);
    setRegistrationToken(token);

    try {
      const response = await apiClient.get('/auth/validateRegistrationToken', {
        params: { id, token },
      });
      setRegistrationToken(response.data.registrationToken);
      setIsMentor(response.data.isMentor);
    } catch (err: any) {
      setRegistrationToken('');
      setError(err.response?.data?.error);
      toast.error(err.response?.data?.error?.message || err.message);
      logger.error(err);
    } finally {
      setIsRegistrationTokenValidated(true);
    }
  };

  useEffect(() => {
    checkURLquery();
  }, []);

  const contactPoints = [
    {
      icon: <PhoneIcon className="text-inherit" />,
      text: `Call us at ${UPGRADE_EDUCATION_PHONE_NUMBER_DISPLAY}`,
      href: `tel:${UPGRADE_EDUCATION_PHONE_NUMBER}`,
    },
    {
      icon: <MailIcon className="text-inherit" />,
      text: `Email us at ${SUPPORT_UPGRADE_EDUCATION_EMAIL}`,
      href: `mailto:${SUPPORT_UPGRADE_EDUCATION_EMAIL}`,
    },
    {
      icon: <DiscordIcon className="text-inherit" />,
      text: 'Instant message us on the Agora Discord',
      href: isMentor ? DISCORD_MENTOR_CHANNEL : DISCORD_STUDENT_CHANNEL,
    },
  ];

  const submitData = () => {
    const errors = validatePassword(password);
    setPasswordErrors(errors);

    if (errors.length > 0) {
      return;
    }

    setLoading(true);

    const data = {
      password,
      id,
      registrationToken,
    };

    apiClient
      .post('/auth/signup', data)
      .then(() => {
        if (!authFlag) {
          history.push('/');
        } else {
          toast.success('Account created successfully. Redirecting to login...');
          setTimeout(() => {
            history.push(BASE_URL);
          }, 500);
        }
      })
      .catch((err) => {
        toast.error(err.response?.data?.message || err.message);
        logger.error(err);
        setLoading(false);
      });
  };

  const action = loading ? (
    <LoadingOutlined />
  ) : (
    <button
      className={`${v3global.buttonPrimary} onboarding-btn`}
      onClick={submitData}
      style={{
        marginBottom: '16px',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <span>Get started</span>
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          marginLeft: '8px',
        }}
      >
        <ArrowRightOutlined /> <HoverShinyEffect />
      </span>
    </button>
  );

  if (!isRegistrationTokenValidated) {
    return (
      <div id="onboarding-container">
        <div id="onboarding-info">
          <h3 className="mb-8">Validating registration token...</h3>
        </div>
      </div>
    );
  }

  if (error || !registrationToken) {
    return ErrorPage(error, contactPoints);
  }

  return (
    <div className={v3global.background} style={{ minHeight: '100vh' }} id="onboarding-container">
      <div id="onboarding-info">
        <h2 className={v3global.text}>Welcome to Agora!</h2>
        <h3 className={v3global.text}>We're glad to have you here!</h3>
        <div id="onboarding-form" style={{ textAlign: 'left', marginTop: '30px' }}>
          <span>*Password:</span>
          <Input.Password
            style={{ marginBottom: '16px' }}
            placeholder="Password"
            value={password}
            onChange={(e) => handlePasswordChange(e.target.value)}
          />

          <span>*Confirm password:</span>
          <Input.Password
            style={{ marginBottom: '16px' }}
            placeholder="Confirm Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />

          {passwordErrors.length > 0 && (
            <ul>
              {passwordErrors.map((error, index) => (
                <li style={{ color: COLORS.ERROR }} key={index}>
                  {error}
                </li>
              ))}
            </ul>
          )}
        </div>
        <br style={{ marginBottom: 10 }} />
        {action}
        <br style={{ marginBottom: 50 }} />
        <Alert
          type="info"
          description={
            <>
              If you're having trouble creating your account and need urgent access, please get in
              touch with us and we'll sort it.
              <div className="flex flex-col gap-4 mt-4">
                {contactPoints.map((contact) => (
                  <a
                    key={contact.href}
                    className="flex items-center gap-4 hover:text-customLila"
                    href={contact.href}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {contact.icon}
                    <span className="font-raleway font-bold ">{contact.text}</span>
                  </a>
                ))}
              </div>
            </>
          }
          showIcon
        />
      </div>
    </div>
  );
};

const ErrorPage = (error: any, contactPoints: any) => {
  const loginLink = (
    <p className="mb-6">
      <a href={`${BASE_URL}/login`} id="onboarding-click-here">
        Click here to log in Agora.
      </a>
    </p>
  );
  if (error?.code === ErrorCodes.USER_ALREADY_ACTIVATED) {
    return (
      <div id="onboarding-container">
        <div id="onboarding-info">
          <h3 className="mb-4">
            The account has already been activated. Please try signing in with your password.
          </h3>
          {loginLink}
          <Alert
            type="info"
            description={
              <>
                If you're having trouble logging in and need to urgently access your account, please
                get in touch with us and we'll sort it.
                <div className="flex flex-col gap-4 mt-4">
                  {contactPoints.map((contact: any) => (
                    <a
                      key={contact.href}
                      className="flex items-center gap-4 hover:text-customLila"
                      href={contact.href}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {contact.icon}
                      <span className="font-raleway font-bold ">{contact.text}</span>
                    </a>
                  ))}
                </div>
              </>
            }
            showIcon
          />
        </div>
      </div>
    );
  }

  if (error?.code === ErrorCodes.USER_IS_DEACTIVATED) {
    return (
      <div id="onboarding-container">
        <div id="onboarding-info">
          <h3 className="mb-4">
            The account has been deactivated. Please get in touch with us to reactivate your
            account.
          </h3>
          {loginLink}
        </div>
      </div>
    );
  }

  if (error.code === ErrorCodes.USER_NOT_FOUND) {
    return (
      <div id="onboarding-container">
        <div id="onboarding-info">
          <h3 className="mb-4">The user account does not exist. Please try with a valid link.</h3>
          {loginLink}
        </div>
      </div>
    );
  }

  return (
    <div id="onboarding-container">
      <div id="onboarding-info">
        <h3 className="mb-4">
          There was an error while processing your request. Please try again later.
        </h3>
        {loginLink}
      </div>
    </div>
  );
};

export default PreOnboardingPage;
